/**
 * EPA-7 — site-level containment for PART-COUNTY nonattainment areas.
 *
 * The Green Book NAYRO table (`epaAttainment.ts`) is county-level: a row with
 * `extent: 'part'` says only part of the county lies in the area. This module
 * asks EPA's own nonattainment-area boundaries whether the site point lies in
 * that part, by a point-in-polygon query against the EPA Office of Air and
 * Radiation (OAR/OAQPS) "Nonattainment Areas and Designations" ArcGIS
 * MapServer on gispub.epa.gov (one polygon layer per NAAQS, "updated weekly
 * from an OAQPS internal database"; access and use constraints: none).
 * Service, layer ids and fields verified live on 30 September 2026.
 *
 * Only CURRENT NONATTAINMENT rows with `extent: 'part'` of a standard EPA
 * publishes a boundary layer for are queried; whole-county rows, maintenance
 * rows and rows of Sulfur Dioxide (1971) and Lead (1978), which have no layer,
 * are never touched and carry no `siteContainment` (for the last, absence
 * reads exactly as 'unknown' would, and a deterministic "no layer" record
 * would only move the SED hash). A county with no queried row makes no
 * request, so the provider stays zero-network for it.
 *
 * Tri-state per queried row (`NaaqsDesignation.siteContainment`):
 *  - 'inside'  — a polygon of that standard's layer containing the point
 *                carries the Green Book area's name and state;
 *  - 'outside' — the point query completed, no polygon containing the point
 *                carries that name, AND the layer does hold a polygon with that
 *                name and state. The last condition means a naming difference
 *                between the Green Book and the GIS layer, or a polygon missing
 *                from the layer, reads 'unknown' — never 'outside';
 *  - 'unknown' — anything else, with a stable `reason`: a failed /
 *                cancelled / malformed / truncated lookup, an area the layer
 *                does not identify, or a private refdata acceptance run
 *                (which reproduces from frozen sources only and has no frozen
 *                copy of this layer).
 *
 * County-level fields never depend on this lookup: a failure leaves every
 * NAYRO field exactly as the table has it, and consumers read 'unknown' as
 * today's county-level answer. Absence of a result is never attainment.
 *
 * Requests go through `fetchWithTimeout` (browser: `/api/proxy`, which
 * allowlists gispub.epa.gov; server: direct), 15 s each including the body,
 * all in parallel. The service's layer list and each layer's area names are
 * shared across sites and cached in memory for six hours; the point query
 * itself is cached by the provider's result cache. A shared load is never
 * bound to one caller's AbortSignal (a cancelled build must not fail the
 * concurrent builds awaiting the same load): it is bounded by its own 15 s
 * deadline, and each caller races its own signal against it.
 */
import { fetchWithTimeout } from './types';
import { raceWithAbort, throwIfAborted } from '../../lib/abortSignal';
import { getPrivateRefdataAcceptanceBinding } from '../refdata/refdataClient';
import { EPA_NONATTAINMENT_AREAS_SERVICE_URL } from '../../lib/epaNonattainmentBoundarySource';
import type { NaaqsDesignation, NaaqsSiteContainment } from './epaAttainment';

/**
 * Green Book standard label → MapServer layer, with the layer name EPA
 * publishes for it. The name is checked against the live service before a
 * layer's answer is trusted, so a renumbered service reads 'unknown' rather
 * than answering from the wrong standard. Sulfur Dioxide (1971) and Lead
 * (1978) have no layer.
 */
export const EPA_NONATTAINMENT_LAYERS: Readonly<Record<string, { id: number; name: string }>> = {
  '8-Hour Ozone (2008)': { id: 1, name: 'Ozone 8-hr (2008 standard)' },
  '8-Hour Ozone (2015)': { id: 2, name: 'Ozone 8-hr (2015 Standard)' },
  'Lead (2008)': { id: 3, name: 'Lead (2008 standard)' },
  'Sulfur Dioxide (2010)': { id: 4, name: 'SO2 1-hr (2010 standard)' },
  'PM-2.5 (2006)': { id: 5, name: 'PM2.5 24hr (2006 standard)' },
  'PM-2.5 (1997)': { id: 6, name: 'PM2.5 Annual (1997 standard)' },
  'PM-2.5 (2012)': { id: 7, name: 'PM2.5 Annual (2012 standard)' },
  'PM-10 (1987)': { id: 8, name: 'PM10 (1987 standard)' },
  'Carbon Monoxide (1971)': { id: 9, name: 'CO (1971 Standard)' },
  'Nitrogen Dioxide (1971)': { id: 11, name: 'NO2 (1971 Standard)' },
};

const REQUEST_TIMEOUT_MS = 15_000;
const METADATA_TTL_MS = 6 * 60 * 60 * 1000;
const OUT_FIELDS = 'area_name,state_abbreviation,pollutant_name';

export function epaNonattainmentLayerUrl(layerId: number): string {
  return `${EPA_NONATTAINMENT_AREAS_SERVICE_URL}/${layerId}`;
}

/**
 * Comparable form of an area name. The Green Book appends the state list
 * ("Denver-Boulder-Greeley-Ft. Collins-Loveland, CO", "…, NY-NJ-CT"); the GIS
 * layer carries the state in its own field. Case, punctuation and "&" differ
 * between the two. Nothing else is stripped: an unmatched name reads 'unknown'.
 */
export function normaliseEpaAreaName(name: string): string {
  return name
    .trim()
    .replace(/,\s*[A-Z]{2}(?:-[A-Z]{2})*$/, '')
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

/** GIS `pollutant_name` for a Green Book label: "8-Hour Ozone (2008)" → "8-Hour Ozone (2008 Standard)". */
function gisPollutantName(naaqs: string): string {
  return naaqs.replace(/\)\s*$/, ' Standard)').toLowerCase();
}

interface BoundaryArea {
  area: string;
  state: string;
}

/**
 * Rejects when `work` has not settled within `ms`. `fetchWithTimeout` bounds
 * the request only until the response headers arrive; this bounds the body
 * read too, so no lookup (and no shared cached load) can stay pending.
 */
function withDeadline<T>(work: Promise<T>, ms: number): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const deadline = new Promise<never>((_, reject) => {
    timer = setTimeout(() => reject(new Error(`EPA nonattainment-area request timed out after ${ms} ms`)), ms);
  });
  return Promise.race([work, deadline]).finally(() => clearTimeout(timer));
}

function fetchArcgisJson(url: string, signal?: AbortSignal): Promise<Record<string, unknown>> {
  return withDeadline((async () => {
    const res = await fetchWithTimeout(url, REQUEST_TIMEOUT_MS, signal);
    if (!res?.ok) throw new Error(`EPA nonattainment-area service HTTP ${res?.status ?? 'no response'}`);
    const json = await res.json() as Record<string, unknown> | null;
    if (!json || typeof json !== 'object') throw new Error('EPA nonattainment-area service returned no JSON object');
    const error = json.error as { message?: string; code?: number } | undefined;
    if (error) throw new Error(`EPA nonattainment-area service error: ${error.message ?? error.code ?? 'unknown'}`);
    return json;
  })(), REQUEST_TIMEOUT_MS);
}

/**
 * Features of one layer's query response, validated: a complete (not
 * truncated) array whose every row names an area, a state and the expected
 * standard. Anything else throws, so the caller reads 'unknown'.
 */
function boundaryAreas(json: Record<string, unknown>, naaqs: string): BoundaryArea[] {
  if (json.exceededTransferLimit === true) throw new Error('EPA nonattainment-area query was truncated');
  const features = json.features;
  if (!Array.isArray(features)) throw new Error('EPA nonattainment-area query returned no features array');
  const expected = gisPollutantName(naaqs);
  return features.map(feature => {
    const attrs = (feature as { attributes?: Record<string, unknown> } | null)?.attributes;
    const area = attrs?.area_name;
    const state = attrs?.state_abbreviation;
    const pollutant = attrs?.pollutant_name;
    if (typeof area !== 'string' || !area.trim() || typeof state !== 'string' || typeof pollutant !== 'string') {
      throw new Error('EPA nonattainment-area feature is missing area, state or pollutant');
    }
    if (pollutant.trim().toLowerCase() !== expected) {
      throw new Error(`EPA nonattainment-area layer answered for "${pollutant}", expected ${naaqs}`);
    }
    return { area, state: state.trim().toUpperCase() };
  });
}

let serviceLayers: { promise: Promise<Map<number, string>>; at: number } | null = null;
const layerAreas = new Map<number, { promise: Promise<BoundaryArea[]>; at: number }>();

/**
 * Cache a shared lookup; a rejected lookup is evicted so the next site
 * retries. `load` must not capture a caller's AbortSignal: every concurrent
 * caller awaits the same promise.
 */
function cached<T>(
  get: () => { promise: Promise<T>; at: number } | null | undefined,
  set: (entry: { promise: Promise<T>; at: number } | null) => void,
  load: () => Promise<T>,
): Promise<T> {
  const hit = get();
  if (hit && Date.now() - hit.at < METADATA_TTL_MS) return hit.promise;
  const entry = { promise: load(), at: Date.now() };
  set(entry);
  entry.promise.catch(() => { if (get() === entry) set(null); });
  return entry.promise;
}

function loadServiceLayers(): Promise<Map<number, string>> {
  return cached(() => serviceLayers, entry => { serviceLayers = entry; }, async () => {
    const json = await fetchArcgisJson(`${EPA_NONATTAINMENT_AREAS_SERVICE_URL}?f=json`);
    if (!Array.isArray(json.layers)) throw new Error('EPA nonattainment-area service lists no layers');
    const names = new Map<number, string>();
    for (const layer of json.layers as Array<{ id?: unknown; name?: unknown }>) {
      if (typeof layer?.id === 'number' && typeof layer.name === 'string') names.set(layer.id, layer.name.trim().toLowerCase());
    }
    return names;
  });
}

function loadLayerAreas(naaqs: string, layerId: number): Promise<BoundaryArea[]> {
  return cached(() => layerAreas.get(layerId), entry => { if (entry) layerAreas.set(layerId, entry); else layerAreas.delete(layerId); }, async () => {
    const url = `${epaNonattainmentLayerUrl(layerId)}/query?where=${encodeURIComponent('1=1')}`
      + `&returnGeometry=false&outFields=${OUT_FIELDS}&f=json`;
    return boundaryAreas(await fetchArcgisJson(url), naaqs);
  });
}

async function queryPoint(naaqs: string, layerId: number, lat: number, lng: number, signal?: AbortSignal): Promise<BoundaryArea[]> {
  const url = `${epaNonattainmentLayerUrl(layerId)}/query?geometry=${lng.toFixed(6)},${lat.toFixed(6)}`
    + '&geometryType=esriGeometryPoint&inSR=4326&spatialRel=esriSpatialRelIntersects'
    + `&returnGeometry=false&outFields=${OUT_FIELDS}&f=json`;
  return boundaryAreas(await fetchArcgisJson(url, signal), naaqs);
}

export interface SiteContainmentResolution {
  /** Copies of the input rows; queried rows carry `siteContainment`. */
  designations: NaaqsDesignation[];
  /** Completion time of the lookups when at least one row resolved inside/outside. */
  resolvedAt: string | null;
  /**
   * True when a lookup failed or was withheld, so the result must not be
   * persisted for the provider's 30-day TTL: the next build retries.
   */
  retryable: boolean;
}

/** Current nonattainment rows that cover part of the county. */
export function isPartCountyNonattainment(d: NaaqsDesignation): boolean {
  return d.status === 'nonattainment' && d.extent === 'part';
}

/** Rows the containment lookup applies to: part-county nonattainment of a standard with an EPA boundary layer. */
function isQueried(d: NaaqsDesignation): boolean {
  return isPartCountyNonattainment(d) && EPA_NONATTAINMENT_LAYERS[d.naaqs] !== undefined;
}

function withContainment(d: NaaqsDesignation, containment: NaaqsSiteContainment): NaaqsDesignation {
  return { ...d, siteContainment: containment };
}

/**
 * Resolve every part-county nonattainment row of a county listing at the site
 * point. Never throws: every failure becomes 'unknown' on the affected rows.
 *
 * @param state USPS code of the county the table row belongs to.
 */
export async function resolvePartCountyContainment(
  designations: readonly NaaqsDesignation[],
  state: string,
  lat: number,
  lng: number,
  signal?: AbortSignal,
): Promise<SiteContainmentResolution> {
  const copies = designations.map(d => ({ ...d }));
  const queried = copies.filter(isQueried);
  if (queried.length === 0) return { designations: copies, resolvedAt: null, retryable: false };

  if (getPrivateRefdataAcceptanceBinding()) {
    return {
      designations: copies.map(d => (isQueried(d)
        ? withContainment(d, { status: 'unknown', reason: 'withheld-private-acceptance' })
        : d)),
      resolvedAt: null,
      retryable: true,
    };
  }

  const stateCode = state.trim().toUpperCase();
  const standards = [...new Set(queried.map(d => d.naaqs))];
  const answers = new Map<string, { layerUrl: string; point: BoundaryArea[]; layer: BoundaryArea[] } | { layerUrl: string; failed: true }>();
  await Promise.all(standards.map(async naaqs => {
    const layer = EPA_NONATTAINMENT_LAYERS[naaqs]!;
    const layerUrl = epaNonattainmentLayerUrl(layer.id);
    try {
      throwIfAborted(signal);
      // The shared loads run without this caller's signal; the caller stops
      // waiting on its own cancellation, and the others keep theirs.
      const [names, layerRows, point] = await Promise.all([
        raceWithAbort(loadServiceLayers(), signal),
        raceWithAbort(loadLayerAreas(naaqs, layer.id), signal),
        queryPoint(naaqs, layer.id, lat, lng, signal),
      ]);
      if (names.get(layer.id) !== layer.name.toLowerCase()) {
        throw new Error(`EPA nonattainment-area layer ${layer.id} is "${names.get(layer.id) ?? 'missing'}", expected "${layer.name}"`);
      }
      answers.set(naaqs, { layerUrl, point, layer: layerRows });
    } catch (err) {
      console.warn(`[epa-attainment] nonattainment-area boundary lookup failed for ${naaqs}: ${err instanceof Error ? err.message : String(err)}`);
      answers.set(naaqs, { layerUrl, failed: true });
    }
  }));

  let resolved = false;
  let retryable = false;
  const result = copies.map(d => {
    const answer = isQueried(d) ? answers.get(d.naaqs) : undefined;
    if (!answer) return d;
    if ('failed' in answer) {
      retryable = true;
      return withContainment(d, { status: 'unknown', reason: 'boundary-lookup-failed', layerUrl: answer.layerUrl });
    }
    const key = normaliseEpaAreaName(d.area);
    const isThisArea = (row: BoundaryArea): boolean => row.state === stateCode && normaliseEpaAreaName(row.area) === key;
    if (answer.point.some(isThisArea)) {
      resolved = true;
      return withContainment(d, { status: 'inside', layerUrl: answer.layerUrl });
    }
    if (answer.layer.some(isThisArea)) {
      resolved = true;
      return withContainment(d, { status: 'outside', layerUrl: answer.layerUrl });
    }
    return withContainment(d, { status: 'unknown', reason: 'boundary-area-not-identified', layerUrl: answer.layerUrl });
  });
  return { designations: result, resolvedAt: resolved ? new Date().toISOString() : null, retryable };
}

/** Test helper — clear the in-memory layer caches. */
export function resetEpaNonattainmentBoundaryCacheForTests(): void {
  serviceLayers = null;
  layerAreas.clear();
}
